/**
 * NX-3941: Frontend mirror of the backend strict number parser
 * (`parseStrictImportedNumber`). Strips currency symbols, thousand separators
 * and spaces, and rejects malformed values (letters, multiple decimal points).
 *
 * Does NOT round — the backend stays the source of truth for precision
 * (NX-3941 decision 2). The frontend only sanitizes + validates.
 *
 * @returns the parsed number, or `null` when the value is empty/invalid.
 *
 * @example
 * parseFormattedNumber('1,500')    // 1500
 * parseFormattedNumber('£500')     // 500
 * parseFormattedNumber('1 500')    // 1500
 * parseFormattedNumber('10.556')   // 10.556 (precision kept, not rounded)
 * parseFormattedNumber('-500')     // -500
 * parseFormattedNumber('abc')      // null
 * parseFormattedNumber('12.34.56') // null
 */
export const parseFormattedNumber = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }

  // Reject strings containing letters other than e/E (scientific notation: 1e3)
  if (/[a-df-zA-DF-Z]/.test(trimmed)) {
    return null;
  }

  // Strip currency symbols, thousand separators and spaces
  const cleaned = trimmed.replace(/[^0-9.eE+-]/g, '');
  if (!cleaned) {
    return null;
  }

  const hasScientific = /[eE]/.test(cleaned);
  // Reject malformed numbers with multiple decimal points (e.g. "12.34.56")
  if (!hasScientific && cleaned.split('.').length > 2) {
    return null;
  }

  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
};
